import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router";
import { getAuthStore, useAuthStore } from "../store/useAuthStore";
import {
  getUserContext,
  getUserCompanies,
  getUserRoleGrants,
  signOut,
  checkHeartBeat,
  refetchAccessToken,
  getPlatformInfo,
  redirectToIdentitySignIn,
  isIdentityRedirectPending,
} from "../api/auth";
import { checkAccessToEsopCompany, getCompanyExists } from "../api/Esop";

export function useGetUserRoleGrants() {
  const { setAuth } = useAuthStore();
  return useQuery({
    queryKey: "userRoleGrants",
    queryFn: getUserRoleGrants,
    onSuccess: (data) => {
      const role = data[0].role || "";
      setAuth({ role });
    },
    staleTime: 60 * 1000,
  });
}

export function useGetAllUserCompanies() {
  return useQuery({
    queryKey: "userCompanies",
    queryFn: getUserCompanies,
    staleTime: 60 * 1000,
  });
}

export function useGetUserContext(enabled: boolean) {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  return useQuery({
    queryKey: "userContext",
    queryFn: getUserContext,
    staleTime: 0,
    cacheTime: 0,
    async onSuccess(data) {
      if (isIdentityRedirectPending()) return;
      // If we've already switched to a company (sessionStorage is the
      // source of truth for that), a userContext response for a different
      // companyId means the backend couldn't build context for it (e.g. a
      // draft with no cap table yet) and fell back to another company —
      // don't let that overwrite the company we're actually on.
      const expectedCompanyId = sessionStorage.getItem("companyId");
      if (
        expectedCompanyId &&
        data.companyId &&
        data.companyId !== expectedCompanyId
      ) {
        return;
      }
      if (data.companyId === "") {
        navigate("/landing/redirect");
      } else {
        let canAccessEsop = false;
        if (data?.isNewEsop && data?.newEsopCompanyId) {
          const companyExists = await checkAccessToEsopCompany(
            data?.newEsopCompanyId?.toString()
          );
          canAccessEsop =
            companyExists?.toLowerCase() === "esop company exists";
        } else if (data?.legacyCompanyId) {
          const companyExists = await getCompanyExists(
            data.legacyCompanyId.toString()
          );
          canAccessEsop = companyExists === "Esop Company Exists";
        }
        if (isIdentityRedirectPending()) return;
        sessionStorage.setItem("companyId", data.companyId || "");
        setAuth({ user: data, companyId: data.companyId, canAccessEsop });
        if (window.location.pathname === "/landing/redirect") {
          navigate("/landing/dashboard");
        }
      }
    },
    enabled,
  });
}

export function useSignOut() {
  return useMutation({
    mutationKey: "signOut",
    mutationFn: signOut,
    onSuccess: redirectToIdentitySignIn,
  });
}

export function useCheckHeartBeat(enabled: boolean) {
  return useQuery({
    queryKey: "heartbeat",
    queryFn: checkHeartBeat,
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
    refetchOnWindowFocus: "always", // Refetch when the tab regains focus
    staleTime: 0, // Ensure data is treated as stale immediately
    cacheTime: 0, // Avoid caching if unnecessary
    retry: false,
    enabled,
  });
}

export function useRefetchAccessToken(enabled: boolean, bootstrapRun = 0) {
  return useQuery({
    queryFn: () => refetchAccessToken(),
    queryKey: ["refetchAccessToken", bootstrapRun],
    enabled,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useGetPlatformInfo(accesstoken: string, enabled?: boolean) {
  return useQuery({
    queryKey: ["getPlatformInfo", accesstoken],
    queryFn: getPlatformInfo,
    enabled,
  });
}
